import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createMerchantOnboardingApi, type MerchantOnboardingApi } from "./api.js";
import type {
  MerchantJobStatusDto,
  MerchantOnboardingActionResult,
  MerchantOnboardingStateDto,
} from "./models.js";

export interface MerchantOnboardingService {
  load(): Promise<MerchantOnboardingStateDto>;
  runAction(action: string): Promise<MerchantOnboardingActionResult>;
  startCatalogSync(
    trigger: "MANUAL" | "RETRY",
  ): Promise<{ job: MerchantJobStatusDto; queued: boolean }>;
  loadJob(jobId: string): Promise<MerchantJobStatusDto>;
}

export interface MerchantOnboardingServiceOptions {
  api?: MerchantOnboardingApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
}

export function createMerchantOnboardingService(
  options: MerchantOnboardingServiceOptions,
): MerchantOnboardingService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? defaultIdempotencyKey;

  return {
    load() {
      return api.getOnboarding();
    },
    runAction(action) {
      return api.runAction(action, idempotencyKeyFactory());
    },
    startCatalogSync(trigger) {
      return api.startCatalogSync(trigger, idempotencyKeyFactory());
    },
    loadJob(jobId) {
      return api.getJob(jobId);
    },
  };
}

function createApiFromClient(client: MerchantApiClient | undefined): MerchantOnboardingApi {
  if (client === undefined) {
    throw new Error("Merchant onboarding service requires an API client.");
  }

  return createMerchantOnboardingApi(client);
}

function defaultIdempotencyKey(): string {
  return `onboarding-${Date.now()}-${crypto.randomUUID()}`;
}
